import React, { useState } from 'react';

/**
 * Fallback book icon when activity icon is unavailable
 */
const BookIcon = ({ fill = '#6c757d', size = 20 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} fill={fill} viewBox="0 0 448 512">
    <path d="M96 0C43 0 0 43 0 96V416c0 53 43 96 96 96H384h32c17.7 0 32-14.3 32-32s-14.3-32-32-32V384c17.7 0 32-14.3 32-32V32c0-17.7-14.3-32-32-32H384 96zm0 384H352v64H96c-17.7 0-32-14.3-32-32s14.3-32 32-32zm32-240c0-8.8 7.2-16 16-16H336c8.8 0 16 7.2 16 16s-7.2 16-16 16H144c-8.8 0-16-7.2-16-16zm16 48H336c8.8 0 16 7.2 16 16s-7.2 16-16 16H144c-8.8 0-16-7.2-16-16s7.2-16 16-16z"/>
  </svg>
);

/**
 * Fallback lightbulb icon when activity icon is unavailable
 */
const LightbulbIcon = ({ fill = '#7c3aed', size = 20 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size * 0.75} height={size} fill={fill} viewBox="0 0 384 512">
    <path d="M272 384c9.6-31.9 29.5-59.1 49.2-86.2c0 0 0 0 0 0c5.2-7.1 10.4-14.2 15.4-21.4c19.8-28.5 31.4-63 31.4-100.3C368 78.8 289.2 0 192 0S16 78.8 16 176c0 37.3 11.6 71.9 31.4 100.3c5 7.2 10.2 14.3 15.4 21.4c0 0 0 0 0 0c19.8 27.1 39.7 54.4 49.2 86.2l160 0zM192 512c44.2 0 80-35.8 80-80l0-16-160 0 0 16c0 44.2 35.8 80 80 80zM112 176c0 8.8-7.2 16-16 16s-16-7.2-16-16c0-61.9 50.1-112 112-112c8.8 0 16 7.2 16 16s-7.2 16-16 16c-44.2 0-80 35.8-80 80z"/>
  </svg>
);

/**
 * Shared Activity Icon component
 *
 * Used across guardian wizard, curriculum editor, kid learning, and progress dialogs
 * to provide consistent activity icon display.
 *
 * @param {string} iconUrl - URL of the activity icon
 * @param {string} name - Alt text for the icon (activity name)
 * @param {number} size - Icon size in pixels (default: 32)
 * @param {boolean} grayscale - Whether to show icon in grayscale (default: false)
 * @param {boolean} showBackground - Whether to wrap icon in colored background (default: false)
 * @param {string} backgroundColor - Background color when showBackground is true (default: '#ede9fe')
 * @param {string} fallback - Fallback icon type: 'book' or 'lightbulb' (default: 'book')
 * @param {string} className - Additional CSS class names
 */
export const ActivityIcon = ({
  iconUrl,
  name,
  size = 32,
  grayscale = false,
  showBackground = false,
  backgroundColor = '#ede9fe',
  fallback = 'book',
  className = ''
}) => {
  const [hasError, setHasError] = useState(false);

  const grayscaleStyle = grayscale ? { filter: 'grayscale(100%)' } : {};
  const fallbackColor = grayscale ? '#9ca3af' : (fallback === 'lightbulb' ? '#7c3aed' : '#6c757d');

  // Determine if we should show the fallback
  const showFallback = !iconUrl || hasError;

  // Render the fallback icon
  const renderFallback = () => {
    if (fallback === 'lightbulb') {
      return <LightbulbIcon fill={fallbackColor} size={showBackground ? size * 0.6 : size} />;
    }
    return <BookIcon fill={fallbackColor} size={showBackground ? size * 0.6 : size} />;
  };

  // Render the actual icon image
  const renderIcon = () => (
    <img
      src={iconUrl}
      alt={name}
      style={{
        width: `${showBackground ? size * 0.6 : size}px`,
        height: `${showBackground ? size * 0.6 : size}px`,
        objectFit: 'contain',
        ...grayscaleStyle
      }}
      onError={() => setHasError(true)}
    />
  );

  // If showBackground is true, wrap in a colored container
  if (showBackground) {
    return (
      <div
        className={className}
        style={{
          width: `${size}px`,
          height: `${size}px`,
          backgroundColor: backgroundColor,
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          flexShrink: 0
        }}
      >
        {showFallback ? renderFallback() : renderIcon()}
      </div>
    );
  }

  // Without background wrapper
  if (showFallback) {
    return (
      <span className={className} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        {renderFallback()}
      </span>
    );
  }

  return (
    <img
      src={iconUrl}
      alt={name}
      className={className}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        objectFit: 'contain',
        ...grayscaleStyle
      }}
      onError={() => setHasError(true)}
    />
  );
};

/**
 * Locked version of ActivityIcon with a lock overlay
 * Used to indicate activities that are not yet available
 */
export const LockedActivityIcon = ({
  iconUrl,
  name,
  size = 40,
  className = ''
}) => {
  const [hasError, setHasError] = useState(false);
  const showFallback = !iconUrl || hasError;

  return (
    <div
      className={className}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        backgroundColor: '#f3f4f6',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        flexShrink: 0
      }}
    >
      {showFallback ? (
        <BookIcon fill="#9ca3af" size={size * 0.85} />
      ) : (
        <img
          src={iconUrl}
          alt={name}
          style={{
            width: `${size * 0.85}px`,
            height: `${size * 0.85}px`,
            objectFit: 'contain',
            filter: 'grayscale(100%)',
            opacity: 0.5
          }}
          onError={() => setHasError(true)}
        />
      )}
      {/* Lock overlay - centered */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'rgba(107, 114, 128, 0.8)',
          borderRadius: '50%',
          width: `${size * 0.5}px`,
          height: `${size * 0.5}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        data-testid="lock-icon"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width={size * 0.25} height={size * 0.25} fill="white" viewBox="0 0 448 512">
          <path d="M144 144v48H304V144c0-44.2-35.8-80-80-80s-80 35.8-80 80zM80 192V144C80 64.5 144.5 0 224 0s144 64.5 144 144v48h16c35.3 0 64 28.7 64 64V448c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V256c0-35.3 28.7-64 64-64H80z"/>
        </svg>
      </div>
    </div>
  );
};

export default ActivityIcon;
